import type { EnvironmentProviders, Provider } from '@angular/core';
import {
  campaignIdsWithExpirationDate,
  superiorIdsWithExpirationDate
} from '@bcf-v2-configurators/tokens/affiliate/lvbetcom/affiliate-ids-with-expiration-date';
import {
  CAMPAIGN_IDS_WITH_EXPIRATION_DATE,
  SUPERIOR_IDS_WITH_EXPIRATION_DATE
} from '@bcf-v2-configurators/tokens/affiliate/token';
import { appMobileCasinoCountryBlocker } from '@bcf-v2-configurators/tokens/app-country-blocker/lvbetcom/app-country-blocker';
import { APP_COUNTRY_BLOCKER } from '@bcf-v2-configurators/tokens/app-country-blocker/token';
import { gamesImagesAssetsUrl } from '@bcf-v2-configurators/tokens/casino/lvbetcom/games-images-assets-url';
import { GAMES_IMAGES_ASSETS_URL } from '@bcf-v2-configurators/tokens/casino/token';
import { USER_PANEL_DISPLAY_TIME_IN_LOCAL_TIME_ZONE } from '@bcf-v2-configurators/tokens/date-format/token';
import { I18N_DESC_LOADER } from '@bcf-v2-configurators/tokens/descs/token';
import { editProfileAddressAvailableFieldsFn } from '@bcf-v2-configurators/tokens/edit-profile/lvbetcom/edit-profile-available-fields';
import { editProfileShowBankAccountInfo } from '@bcf-v2-configurators/tokens/edit-profile/lvbetcom/edit-profile-show-bank-account-info';
import {
  EDIT_PROFILE_ADDRESS_AVAILABLE_FIELDS,
  EDIT_PROFILE_SHOW_BANK_ACCOUNT_INFO
} from '@bcf-v2-configurators/tokens/edit-profile/token';
import { footerLinksFn } from '@bcf-v2-configurators/tokens/footer/lvbetcom/casino-footer-links';
import { spSidebarLinksFn } from '@bcf-v2-configurators/tokens/footer/lvbetcom/casino-sp-sidebar-links';
import { footerBrandName } from '@bcf-v2-configurators/tokens/footer/lvbetcom/footer-brand-name';
import { FOOTER_BRAND_NAME, FOOTER_LINKS, SP_SIDEBAR_LINKS } from '@bcf-v2-configurators/tokens/footer/token';
import { imgWebConfig } from '@bcf-v2-configurators/tokens/img-webp/lvbetcom/img-web-config';
import { IMG_WEB_CONFIG } from '@bcf-v2-configurators/tokens/img-webp/token';
import { introHasCasinoComponents } from '@bcf-v2-configurators/tokens/intro/lvbetcom/intro-has-casino-components';
import { INTRO_HAS_CASINO_COMPONENTS } from '@bcf-v2-configurators/tokens/intro/token';
import { IPQS_REGISTER_IP_CHECK_ENABLED } from '@bcf-v2-configurators/tokens/ipqs/token';
import { ladeskChatAdditionalConfig } from '@bcf-v2-configurators/tokens/ladesk-chat/lvbetcom/ladesk-chat-additional-config';
import { LADESK_CHAT_ADDITIONAL_CONFIG } from '@bcf-v2-configurators/tokens/ladesk-chat/token';
import { lvbetcomLanguageSwitcherFlagMap } from '@bcf-v2-configurators/tokens/language-switcher/lvbetcom/lvbetcom-language-switcher-flag-map';
import { LANGUAGE_SWITCHER_FLAG_MAP } from '@bcf-v2-configurators/tokens/language-switcher/token';
import { lotteryBrandName } from '@bcf-v2-configurators/tokens/lottery/lvbet/lottery-brand-name';
import { LOTTERY_BRAND_NAME } from '@bcf-v2-configurators/tokens/lottery/token';
import { lvWheelConfig } from '@bcf-v2-configurators/tokens/lv-wheel/lvbetcom/lv-wheel-config';
import { lotteryAssetsUrlValue } from '@bcf-v2-configurators/tokens/lv-wheel/lvbetpl/lottery-assets-url';
import { LV_WHEEL_CONFIG } from '@bcf-v2-configurators/tokens/lv-wheel/token';
import { modalCookiesShowIndicator } from '@bcf-v2-configurators/tokens/modals/lvbetcom/modal-cookies-show-indicator';
import { modalNewTermsAlternativeTitle } from '@bcf-v2-configurators/tokens/modals/lvbetcom/modal-new-terms-alternative-title';
import { modalNewTermsShowVersionLink } from '@bcf-v2-configurators/tokens/modals/lvbetcom/modal-new-terms-show-version-link';
import {
  MODAL_COOKIES_INDICATOR,
  MODAL_NEW_TERMS_ALTERNATIVE_TITLE,
  MODAL_NEW_TERMS_SHOW_VERSION_LINK
} from '@bcf-v2-configurators/tokens/modals/token';
import { OPTIMOVE_SDK_CONFIG_LOADER } from '@bcf-v2-configurators/tokens/optimove-sdk/tokens';
import { piqStyles } from '@bcf-v2-configurators/tokens/piq-styles/lvbetcom/piq-styles';
import { piqTheme } from '@bcf-v2-configurators/tokens/piq-styles/lvbetcom/piq-theme';
import { PIQ_CSS, PIQ_THEME } from '@bcf-v2-configurators/tokens/piq-styles/token';
import { REGISTER_ENABLED } from '@bcf-v2-configurators/tokens/register/token';
import { RG_LIMITS_PARALLEL_TIME_VALUES_API_KIND } from '@bcf-v2-configurators/tokens/rg-limits-parallel/token';
import { sportsLinkMobileWeb } from '@bcf-v2-configurators/tokens/sports-links/lvbetcom/sports-links';
import { SPORTS_FULL_URL } from '@bcf-v2-configurators/tokens/sports-links/token';
import { provideVanillaTsTokens } from '@bcf-v2-shared/misc/vanilla-ts-providers/provide-vanilla-ts-tokens';
import { logRocketApiKey } from '@bcf-vanilla-ts-v1-configurators/tokens/log-rocket/lvbetcom/log-rocket-api-key';
import { registerLogRocketApiKey } from '@bcf-vanilla-ts-v1-configurators/tokens/log-rocket/token';
import { LOTTERY_ASSETS_URL } from '@bcf-vanilla-ts-v1-configurators/tokens/lottery/token';
import { rgLimitsParallelTimeValuesApiKind } from '@bcf-vanilla-ts-v1-configurators/tokens/rg-limits-parallel/lvbetcom/rg-limits-parallel-time-values-api-kind';
import { cpSortedProviderNamesToOrder } from '@bcf-vanilla-ts-v1-configurators/tokens/slots/lvbetcom/cp-sorted-provider-ids-to-order';
import { SORTED_PROVIDERS_IDS_TO_ORDER } from '@bcf-vanilla-ts-v1-configurators/tokens/slots/token';

export const settingsProviders: (Provider | EnvironmentProviders)[] = [
  {
    provide: IMG_WEB_CONFIG,
    useValue: imgWebConfig
  },
  {
    provide: I18N_DESC_LOADER,
    useValue: () =>
      import(
        /* webpackChunkName: "const-i18n-desc" */ '@bcf-v2-configurators/tokens/descs/lvbetcom/mobile-web/i18n-desc'
        // eslint-disable-next-line @typescript-eslint/typedef
      ).then((mod) => mod.i18nDesc)
  },
  {
    provide: GAMES_IMAGES_ASSETS_URL,
    useValue: gamesImagesAssetsUrl
  },
  {
    provide: EDIT_PROFILE_ADDRESS_AVAILABLE_FIELDS,
    useValue: editProfileAddressAvailableFieldsFn
  },
  {
    provide: EDIT_PROFILE_SHOW_BANK_ACCOUNT_INFO,
    useValue: editProfileShowBankAccountInfo
  },
  {
    provide: LADESK_CHAT_ADDITIONAL_CONFIG,
    useValue: ladeskChatAdditionalConfig
  },
  {
    provide: IPQS_REGISTER_IP_CHECK_ENABLED,
    useValue: false
  },
  {
    provide: REGISTER_ENABLED,
    useValue: false
  },
  {
    provide: LANGUAGE_SWITCHER_FLAG_MAP,
    useValue: lvbetcomLanguageSwitcherFlagMap
  },
  {
    provide: SPORTS_FULL_URL,
    useValue: sportsLinkMobileWeb
  },
  {
    provide: FOOTER_LINKS,
    useValue: footerLinksFn
  },
  {
    provide: SP_SIDEBAR_LINKS,
    useValue: spSidebarLinksFn
  },
  {
    provide: FOOTER_BRAND_NAME,
    useValue: footerBrandName
  },
  {
    provide: LV_WHEEL_CONFIG,
    useValue: lvWheelConfig
  },
  {
    provide: LOTTERY_ASSETS_URL,
    useValue: lotteryAssetsUrlValue
  },
  {
    provide: RG_LIMITS_PARALLEL_TIME_VALUES_API_KIND,
    useValue: rgLimitsParallelTimeValuesApiKind
  },
  {
    provide: SORTED_PROVIDERS_IDS_TO_ORDER,
    useValue: cpSortedProviderNamesToOrder
  },
  {
    provide: OPTIMOVE_SDK_CONFIG_LOADER,
    useValue: () =>
      import('@bcf-v2-configurators/tokens/optimove-sdk/lvbetcom/casino/mobile-web/optimove-sdk-config').then(
        // eslint-disable-next-line @typescript-eslint/typedef
        (mod) => mod.optimoveSdkConfigs
      )
  },
  {
    provide: MODAL_NEW_TERMS_SHOW_VERSION_LINK,
    useValue: modalNewTermsShowVersionLink
  },
  {
    provide: MODAL_NEW_TERMS_ALTERNATIVE_TITLE,
    useValue: modalNewTermsAlternativeTitle
  },
  {
    provide: MODAL_COOKIES_INDICATOR,
    useValue: modalCookiesShowIndicator
  },
  { provide: PIQ_THEME, useValue: piqTheme },
  { provide: PIQ_CSS, useValue: piqStyles },
  {
    provide: CAMPAIGN_IDS_WITH_EXPIRATION_DATE,
    useValue: campaignIdsWithExpirationDate
  },
  {
    provide: SUPERIOR_IDS_WITH_EXPIRATION_DATE,
    useValue: superiorIdsWithExpirationDate
  },
  {
    provide: APP_COUNTRY_BLOCKER,
    useValue: appMobileCasinoCountryBlocker
  },
  {
    provide: USER_PANEL_DISPLAY_TIME_IN_LOCAL_TIME_ZONE,
    useValue: true
  },
  provideVanillaTsTokens(() => {
    registerLogRocketApiKey(logRocketApiKey);
  }),
  {
    provide: LOTTERY_BRAND_NAME,
    useValue: lotteryBrandName
  },
  {
    provide: INTRO_HAS_CASINO_COMPONENTS,
    useValue: introHasCasinoComponents
  }
];
